import type { z } from 'zod';
import { ApiFailure, failureFromBody } from './errors';
import {
  Availability,
  Booking,
  BookingCancelled,
  BookingList,
  Me,
  OtpRequested,
  OtpVerified,
  SessionEnded,
  SiteResponse,
} from '../wire';
import type { CreateBooking } from '../wire';

/**
 * One thin wrapper over `fetch`, and every request this bundle makes goes
 * through it.
 *
 * Three properties, each of which is a decision made elsewhere arriving here:
 *
 * **Same origin, always.** The load balancer sends `/api/*` on a tenant
 * hostname to `guest-api` and everything else to the SPA's bucket (spec §6.4.2),
 * so the base URL is empty and there is no configuration for it. That is also
 * what makes the cookie work: a host-only cookie set by `book.lovespa.com` is
 * sent to `book.lovespa.com`, and a cross-origin API would have needed either a
 * `Domain` attribute — the exact cross-tenant link §6.4.1 exists to not create —
 * or CORS on a public write surface.
 *
 * **`credentials: 'include'`, and never a cookie read.** The session cookie is
 * `httpOnly`; this code could not read it if it wanted to. "Am I signed in" is
 * answered by `/api/me` and by the `session` block on `/api/site`, which is why
 * `site()` and `me()` exist and no `getCookie` does.
 *
 * **No token refresh, no retry.** `apps/admin`'s client retries once on a `401`
 * because Firebase rotates ID tokens hourly and a laptop tab outlives one. A
 * guest session has nothing to refresh: a `401` means the cookie is over, and
 * the answer is the OTP flow, on screen, with the half-filled booking still
 * visible. Retrying would hide the one event the guest has to act on.
 */

export interface RequestSpec<T> {
  method: 'GET' | 'POST';
  /** Under `/api`, leading slash: `/site`, `/bookings`. */
  path: string;
  query?: Record<string, string | number | undefined> | undefined;
  body?: unknown;
  schema: z.ZodType<T>;
  /** Sent as `Idempotency-Key` (spec §5.3; `guest-api/src/intents.ts`). */
  idempotencyKey?: string | undefined;
  signal?: AbortSignal | undefined;
}

export interface GuestApiOptions {
  /** Empty for same-origin, which is the only value production ever uses. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

export class GuestApi {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: GuestApiOptions = {}) {
    this.baseUrl = options.baseUrl ?? '';
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  url(path: string, query?: Record<string, string | number | undefined>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined && value !== '') search.set(key, String(value));
    }
    const qs = search.toString();
    return `${this.baseUrl}/api${path}${qs === '' ? '' : `?${qs}`}`;
  }

  async request<T>(spec: RequestSpec<T>): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (spec.body !== undefined) headers['content-type'] = 'application/json';
    if (spec.idempotencyKey !== undefined) headers['Idempotency-Key'] = spec.idempotencyKey;

    let response: Response;
    try {
      response = await this.fetchImpl(this.url(spec.path, spec.query), {
        method: spec.method,
        headers,
        // The whole authentication scheme, in one option.
        credentials: 'include',
        ...(spec.body === undefined ? {} : { body: JSON.stringify(spec.body) }),
        ...(spec.signal === undefined ? {} : { signal: spec.signal }),
      });
    } catch (e) {
      throw ApiFailure.offline(String(e));
    }

    const text = await response.text();
    const json = parseJson(text);

    if (!response.ok) {
      const retryAfter = Number(response.headers.get('retry-after'));
      throw failureFromBody(
        response.status,
        json,
        Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
      );
    }

    const parsed = spec.schema.safeParse(json);
    if (!parsed.success) {
      throw new ApiFailure('malformed', 'This page received an answer it did not understand.', {
        status: response.status,
        detail: { issues: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`) },
      });
    }
    return parsed.data;
  }

  /* ---------------------------------------------------------------- */
  /* The nine routes of the guest registry, and no tenth               */
  /* ---------------------------------------------------------------- */

  /** Who am I — answered from the Host header. The first request the page makes. */
  site(signal?: AbortSignal): Promise<SiteResponse> {
    return this.request({ method: 'GET', path: '/site', schema: SiteResponse, signal });
  }

  availability(
    query: {
      service_id?: string | undefined;
      date_from: string;
      date_to: string;
      party_size?: number | undefined;
      limit?: number | undefined;
    },
    signal?: AbortSignal,
  ): Promise<Availability> {
    return this.request({
      method: 'GET',
      path: '/availability',
      query,
      schema: Availability,
      signal,
    });
  }

  requestOtp(identity: string): Promise<OtpRequested> {
    // `identity_kind` is deliberately not sent: `guest-api` infers it from an
    // `@` and normalizes with the domain's own function, so that
    // "+1 (340) 555-1212" and "+13405551212" are one guest. Classifying it here
    // would be a second normalization rule living in a browser.
    return this.request({
      method: 'POST',
      path: '/otp/request',
      body: { identity },
      schema: OtpRequested,
    });
  }

  verifyOtp(input: { identity: string; code: string; name?: string }): Promise<OtpVerified> {
    return this.request({
      method: 'POST',
      path: '/otp/verify',
      body: {
        identity: input.identity,
        code: input.code,
        ...(input.name === undefined || input.name === '' ? {} : { name: input.name }),
      },
      schema: OtpVerified,
    });
  }

  me(signal?: AbortSignal): Promise<Me> {
    return this.request({ method: 'GET', path: '/me', schema: Me, signal });
  }

  bookings(signal?: AbortSignal): Promise<BookingList> {
    return this.request({ method: 'GET', path: '/bookings', schema: BookingList, signal });
  }

  /**
   * Create a booking. The body is `CreateBooking` and `CreateBooking` has no
   * price — see its note in `wire.ts`.
   *
   * The intent id doubles as the idempotency key (`readIntent` accepts either),
   * and it is generated once per attempt by the caller rather than here, so that
   * a guest tapping "Confirm" twice on a slow connection sends the *same* key
   * and gets one booking with two identical answers.
   */
  createBooking(body: CreateBooking): Promise<Booking> {
    return this.request({
      method: 'POST',
      path: '/bookings',
      body,
      schema: Booking,
      idempotencyKey: body.client_intent_id,
    });
  }

  cancelBooking(bookingId: string, clientIntentId?: string): Promise<BookingCancelled> {
    return this.request({
      method: 'POST',
      path: `/bookings/${encodeURIComponent(bookingId)}/cancel`,
      body: clientIntentId === undefined ? {} : { client_intent_id: clientIntentId },
      schema: BookingCancelled,
      idempotencyKey: clientIntentId,
    });
  }

  /** Anonymous by design — signing out must work with a cookie the server refuses. */
  endSession(): Promise<z.infer<typeof SessionEnded>> {
    return this.request({ method: 'POST', path: '/session/end', body: {}, schema: SessionEnded });
  }
}

function parseJson(text: string): unknown {
  if (text.length === 0) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
